import { Fragment } from 'react'
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'

import SVGWrapper from '../../../../components/SVGWrapper'
import { IconEdit } from '../../../../assets/icons'
import { FONT_FAMILY } from '../../../../components/AdviceSummary/shared'
import { cardBorderColor, ArrowGlyph, MobileCardFrame } from '../../../../components/advice/shared'

// The entire card is clickable when editable, so render it as a button while
// resetting the browser's default button typography and alignment.
const Card = styled(MobileCardFrame)`
  position: relative;
  display: block;
  width: 100%;
  padding: 12px 16px 24px;
  background: #ffffff;
  font: inherit;
  color: inherit;
  text-align: inherit;

  &:is(button) {
    cursor: pointer;
  }
`

const Header = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 20px;
  padding: 0 28px;
`

const Title = styled.div`
  font-family: ${FONT_FAMILY};
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.darkestGray};
  text-align: center;
`

// The card handles editing, so keep the pencil non-interactive to avoid nesting
// a button inside another button.
const EditIcon = styled.span`
  position: absolute;
  top: -2px;
  right: -4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  color: ${p => p.theme.lightestGray};
`

const SubRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 18px;
  padding-top: 20px;
`

const SubLabel = styled.div`
  font-family: ${FONT_FAMILY};
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.mediumGray};
  text-align: center;
`

// Align the arrow with the first value line when a percentage appears below it.
const ValueRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  column-gap: 8px;
  width: 100%;
`

// Keep each value line from wrapping; an optional secondary metric appears
// beneath the primary value.
const valueBase = `
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 2px;
  padding-top: 2px;
  font-family: ${FONT_FAMILY};
  font-size: 15px;
  line-height: 20px;
  text-align: center;
  white-space: nowrap;
`

const CurrentValue = styled.div`
  ${valueBase};
  color: ${p => p.theme.mediumGray};
`

// Unchanged and qualitative values (risk level) are de-emphasized.
const ModifiedValue = styled.div`
  ${valueBase};
  font-weight: ${p => (p.regular ? 400 : 600)};
  color: ${p => p.theme.primaryColor};
`

const Arrow = styled(ArrowGlyph)`
  flex: 0 0 auto;
  color: ${p => p.theme.primaryColor};
`

const Divider = styled.div`
  height: 2px;
  margin: 24px 14px 0;
  background: ${cardBorderColor};
`

/**
 * Renders a summary section as a mobile card with current and modified values.
 * When `onEdit` is provided, the entire card acts as the edit control.
 */
const MobileSection = ({ title, onEdit, editAriaLabel, rows }) => {
  const theme = useTheme()

  return (
    <Card
      as={onEdit ? 'button' : 'div'}
      type={onEdit ? 'button' : undefined}
      theme={theme}
      onClick={onEdit}
      aria-label={onEdit ? (editAriaLabel ?? `Edit ${title}`) : undefined}>
      <Header>
        <Title theme={theme}>{title}</Title>

        {onEdit && (
          <EditIcon theme={theme} aria-hidden='true'>
            <SVGWrapper svg={IconEdit} size='mediumLarge' />
          </EditIcon>
        )}
      </Header>

      {rows.map((row, index) => (
        <Fragment key={row.key}>
          {index > 0 && <Divider theme={theme} role='separator' />}

          <SubRow>
            {row.subLabel && <SubLabel theme={theme}>{row.subLabel}</SubLabel>}

            <ValueRow>
              <CurrentValue theme={theme}>{row.current}</CurrentValue>
              <Arrow theme={theme} />
              <ModifiedValue theme={theme} regular={row.modifiedIsRegularWeight}>
                {row.modified}
              </ModifiedValue>
            </ValueRow>
          </SubRow>
        </Fragment>
      ))}
    </Card>
  )
}

MobileSection.propTypes = {
  title: PropTypes.string.isRequired,
  /** Makes the card editable; omit to render it as read-only. */
  onEdit: PropTypes.func,
  /** Defaults to `Edit {title}`. */
  editAriaLabel: PropTypes.string,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      subLabel: PropTypes.node,
      current: PropTypes.node.isRequired,
      modified: PropTypes.node.isRequired,
      /** Uses regular rather than emphasized weight for the modified value. */
      modifiedIsRegularWeight: PropTypes.bool,
    })
  ).isRequired,
}

export default MobileSection
